import { useState, useEffect, useRef } from 'react';
import { Loader2, Upload, Save, Eye, Image as ImageIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import api from '@/utils/api';
import { resolveUploadUrl } from '@/utils/helpers';

const DEFAULTS = {
  home_hero_image: 'https://images.unsplash.com/photo-1579871494447-9811cf80d66c?auto=format&fit=crop&w=800&q=60',
  home_title: 'Sushi premium a tu puerta',
  home_subtitle: 'Pedí en Asunción con entrega rápida',
  home_secondary_text: '',
  home_overlay: 'true',
};

export default function HomeConfigPage() {
  const [form, setForm] = useState({ ...DEFAULTS });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/settings/home').then(r => {
      setForm(prev => ({ ...prev, ...r.data }));
      if (r.data.home_hero_image) {
        setPreviewUrl(resolveUploadUrl(r.data.home_hero_image));
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Imagen muy grande (máx. 5MB)'); return; }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { toast.error('Solo jpg, png, webp'); return; }

    setUploading(true);
    const fd = new FormData();
    fd.append('image', file);

    try {
      const { data } = await api.post('/settings/home/hero', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      set('home_hero_image', data.url);
      const base = process.env.REACT_APP_BACKEND_URL || '';
      setPreviewUrl(`${base}${data.url}?t=${Date.now()}`);
      toast.success('Imagen de portada actualizada');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al subir imagen');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/home', {
        home_title: form.home_title,
        home_subtitle: form.home_subtitle,
        home_secondary_text: form.home_secondary_text,
        home_overlay: form.home_overlay,
      });
      toast.success('Configuración guardada');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const heroSrc = previewUrl || (form.home_hero_image?.startsWith('http') ? form.home_hero_image : DEFAULTS.home_hero_image);
  const showOverlay = form.home_overlay === 'true';

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="animate-spin text-zinc-500" size={24} />
    </div>
  );

  return (
    <div data-testid="home-config-page" className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-lg font-bold">Configuración del Home</h2>
        <p className="text-sm text-zinc-500 mt-1">Editá la portada que ven los clientes</p>
      </div>

      {/* Preview */}
      <div data-testid="home-preview" className="relative h-48 md:h-56 rounded-xl overflow-hidden border border-zinc-800">
        {showOverlay && <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a]/60 to-[#0a0a0a] z-10" />}
        <img src={heroSrc} alt="Hero preview" className="w-full h-full object-cover" />
        <div className="absolute bottom-4 left-4 right-4 z-20">
          <h3 className="text-2xl md:text-3xl font-bold tracking-tight leading-tight" dangerouslySetInnerHTML={{ __html: (form.home_title || DEFAULTS.home_title).replace(/\*(.*?)\*/g, '<span class="text-[#E84C1C]">$1</span>') }} />
          <p className="text-zinc-400 text-sm mt-1">{form.home_subtitle || DEFAULTS.home_subtitle}</p>
          {form.home_secondary_text && <p className="text-zinc-500 text-xs mt-1">{form.home_secondary_text}</p>}
        </div>
        <div className="absolute top-3 right-3 z-20 bg-black/50 backdrop-blur-sm text-zinc-300 text-[10px] px-2 py-1 rounded-full flex items-center gap-1">
          <Eye size={11} /> Vista previa
        </div>
      </div>

      {/* Hero image upload */}
      <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-4 space-y-3">
        <Label className="text-sm font-medium flex items-center gap-2"><ImageIcon size={15} className="text-zinc-500" /> Imagen de portada</Label>
        <div className="flex items-center gap-3">
          <button
            data-testid="hero-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-zinc-800 border border-zinc-700 text-sm hover:bg-zinc-700 transition-colors disabled:opacity-50"
          >
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            {uploading ? 'Subiendo...' : 'Cambiar imagen'}
          </button>
          <span className="text-xs text-zinc-600">JPG, PNG, WebP. Máx. 3MB</span>
        </div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageUpload} className="hidden" />
      </div>

      {/* Text fields */}
      <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-4 space-y-4">
        <div>
          <Label className="text-zinc-400 text-xs">Título principal</Label>
          <Input
            data-testid="home-title-input"
            value={form.home_title}
            onChange={e => set('home_title', e.target.value)}
            placeholder={DEFAULTS.home_title}
            className="bg-zinc-800 border-zinc-700 h-10 mt-1"
          />
          <p className="text-[10px] text-zinc-600 mt-1">Usá *asteriscos* para resaltar en naranja. Ej: Sushi *premium* a tu puerta</p>
        </div>
        <div>
          <Label className="text-zinc-400 text-xs">Subtítulo</Label>
          <Input
            data-testid="home-subtitle-input"
            value={form.home_subtitle}
            onChange={e => set('home_subtitle', e.target.value)}
            placeholder={DEFAULTS.home_subtitle}
            className="bg-zinc-800 border-zinc-700 h-10 mt-1"
          />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs">Texto secundario (opcional)</Label>
          <Textarea
            data-testid="home-secondary-input"
            value={form.home_secondary_text}
            onChange={e => set('home_secondary_text', e.target.value)}
            placeholder="Ej: Abierto de 11:00 a 23:00"
            rows={2}
            className="bg-zinc-800 border-zinc-700 mt-1 resize-none"
          />
        </div>
        <div className="flex items-center justify-between py-1">
          <div>
            <Label className="text-zinc-300 text-sm">Overlay oscuro</Label>
            <p className="text-[10px] text-zinc-600">Oscurece la imagen para mejor legibilidad</p>
          </div>
          <Switch
            data-testid="home-overlay-switch"
            checked={showOverlay}
            onCheckedChange={v => set('home_overlay', v ? 'true' : 'false')}
          />
        </div>
      </div>

      {/* Save */}
      <button
        data-testid="home-config-save"
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E84C1C] text-white font-semibold hover:bg-[#d43f12] active:scale-[0.98] transition-all disabled:opacity-50"
      >
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        Guardar configuración
      </button>
    </div>
  );
}
